import type { components } from "@brandex/api-client";
import { useBrandex } from "@brandex/dashboard";
import { formatTaka } from "@brandex/i18n";
import { Badge, Button, Field, Input, Select, Spinner } from "@brandex/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { useWebsites } from "../data";
import { QueryError } from "../views";
import { dhakaWhen } from "./schedule";

type Appointment = components["schemas"]["AppointmentDto"];
type Status = Appointment["status"];

type PaymentStatus = NonNullable<components["schemas"]["AppointmentDto"]["payment"]>["status"];
const PAYMENT: Record<PaymentStatus, string> = {
  pending: "Awaiting payment",
  paid: "Paid",
  failed: "Payment failed",
  expired: "Payment expired",
  refund_due: "Refund due",
};
const STATUS: Record<Status, { label: string; tone: "success" | "warning" | "neutral" }> = {
  pending: { label: "Pending", tone: "warning" },
  confirmed: { label: "Confirmed", tone: "success" },
  declined: { label: "Declined", tone: "neutral" },
  cancelled: { label: "Cancelled", tone: "neutral" },
};
const key = ["admin", "appointments"] as const;

/**
 * Live bookings (F-08): every doctor's appointments from a day on, with
 * confirm, decline or cancel on the practice's behalf (audited).
 */
export function BookingsPage() {
  const { api } = useBrandex();
  const qc = useQueryClient();
  const clients = useWebsites();
  const [org, setOrg] = useState("");
  const [status, setStatus] = useState<"" | Status>("");
  const [from, setFrom] = useState("");
  const list = useQuery({
    queryKey: [...key, org, status, from],
    queryFn: async () => {
      const { data } = await api.http.GET("/v1/admin/appointments", {
        params: { query: { ...(org === "" ? {} : { org_id: org }), ...(status === "" ? {} : { status }), ...(from === "" ? {} : { from }) } },
      });
      if (data === undefined) throw new Error("Empty response");
      return data;
    },
    refetchInterval: 60_000,
  });
  const decide = useMutation({
    mutationFn: async ({ id, to }: { id: string; to: "confirmed" | "declined" | "cancelled" }) => {
      await api.http.POST("/v1/admin/appointments/{appointment_id}/status", { params: { path: { appointment_id: id } }, body: { status: to } });
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: key }),
  });
  return (
    <div className="grid gap-6">
      <header>
        <h1 className="font-display text-3xl font-bold tracking-tight">Live bookings</h1>
        <p className="mt-1 text-sm text-muted-foreground">Appointments patients booked on doctor portfolios, in Bangladesh time. Changes are recorded in the audit log.</p>
      </header>
      <div className="flex flex-wrap items-start gap-3">
        <Field label="Client">
          <Select className="min-w-48" value={org} onChange={(e) => setOrg(e.target.value)}>
            <option value="">All clients</option>
            {(clients.data ?? []).map((c) => <option key={c.organization_id} value={c.organization_id}>{c.doctor_name ?? c.organization_id}</option>)}
          </Select>
        </Field>
        <Field label="Status">
          <Select className="min-w-48" value={status} onChange={(e) => setStatus(e.target.value as "" | Status)}>
            <option value="">Any status</option>
            {(Object.keys(STATUS) as Status[]).map((s) => <option key={s} value={s}>{STATUS[s].label}</option>)}
          </Select>
        </Field>
        <Field label="From date">
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </Field>
      </div>
      {list.isPending ? <Spinner label="Loading…" /> : null}
      {list.isError ? <QueryError error={list.error} /> : null}
      {decide.isError ? <QueryError error={decide.error} /> : null}
      {list.data !== undefined ? (
        <div className="glass overflow-x-auto rounded-[var(--radius-lg)]">
          <table className="w-full min-w-[860px] text-sm">
            <thead className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr><th className="px-4 py-3">When</th><th>Doctor</th><th>Patient</th><th>Chamber</th><th>Reason</th><th>Status</th><th className="px-4" /></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {list.data.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">No bookings.</td></tr>
              ) : (
                list.data.map((a) => {
                  const when = dhakaWhen(a.starts_at);
                  return (
                    <tr key={a.id}>
                      <td className="px-4 py-3"><p>{when.date}</p><p className="text-xs text-muted-foreground">{when.time}</p>{a.reference ? <p className="text-xs text-muted-foreground">{a.reference}</p> : null}</td>
                      <td>{a.doctor_name ?? "—"}</td>
                      <td><p className="font-medium">{a.patient_name}</p><p className="text-xs text-muted-foreground">{a.patient_phone}</p></td>
                      <td>{a.chamber_name ?? "—"}</td>
                      <td>{a.reason || "—"}</td>
                      <td>
                        <Badge tone={STATUS[a.status].tone}>{STATUS[a.status].label}</Badge>
                        {a.payment ? <p className="mt-1 text-xs text-muted-foreground">{PAYMENT[a.payment.status]} · {formatTaka("en", a.payment.amount_paisa)}</p> : null}
                        {a.cancelled_by ? <p className="text-xs text-muted-foreground">Cancelled by {a.cancelled_by === "unpaid" ? "non-payment" : a.cancelled_by}</p> : null}
                        {a.reschedules > 0 ? <p className="text-xs text-muted-foreground">Moved {a.reschedules}×</p> : null}
                      </td>
                      <td className="px-4 text-right">
                        {a.status === "pending" ? (
                          <>
                            <Button size="sm" variant="ghost" disabled={a.payment?.status === "pending"} aria-label={`Confirm ${a.patient_name}`} onClick={() => decide.mutate({ id: a.id, to: "confirmed" })}>Confirm</Button>
                            <Button size="sm" variant="ghost" aria-label={`Decline ${a.patient_name}`} onClick={() => decide.mutate({ id: a.id, to: "declined" })}>Decline</Button>
                          </>
                        ) : a.status === "confirmed" ? (
                          <Button size="sm" variant="ghost" aria-label={`Cancel ${a.patient_name}`} onClick={() => decide.mutate({ id: a.id, to: "cancelled" })}>Cancel</Button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
